import { defaultSettings } from "./factories";
import { normalizeSocialBlockLinks } from "./media";
import { isGroup } from "./sections";
import {
  normalizeButtonBlock,
  normalizeButtonRowSlot,
} from "./button-theme";
import {
  normalizeDividerBlock,
  normalizeEyebrowBlock,
  normalizeTextBlock,
} from "./text-theme";
import type {
  Block,
  DocumentSection,
  EmailDocument,
  EmailSettings,
  Row,
} from "./types";
import { EMAIL_DOCUMENT_SCHEMA_VERSION } from "./types";
import { DEFAULT_IMAGE_OVERLAY } from "./image-overlay";

function normalizeSettings(incoming: Partial<EmailSettings>): EmailSettings {
  return {
    ...defaultSettings(),
    ...incoming,
  };
}

function normalizeBlock(
  block: Block,
  settings: EmailDocument['settings'],
): Block {
  if (block.type === 'text') {
    return normalizeTextBlock(block, settings);
  }
  if (block.type === 'divider') {
    return normalizeDividerBlock(block, settings);
  }
  if (block.type === 'eyebrow') {
    return normalizeEyebrowBlock(block, settings);
  }
  if (block.type === 'button') {
    return normalizeButtonBlock(block, settings);
  }
  if (block.type === 'buttonRow') {
    return {
      ...block,
      buttons: block.buttons.map(slot =>
        normalizeButtonRowSlot(slot, settings),
      ) as typeof block.buttons,
    };
  }
  if (block.type === 'social') {
    return {
      ...block,
      iconVariant: block.iconVariant ?? 'white',
      iconSize: block.iconSize ?? 16,
      links: normalizeSocialBlockLinks(block.links),
    };
  }
  if (block.type === 'image') {
    return {
      ...block,
      intrinsicSize: block.intrinsicSize ?? null,
      imageOverlay: block.imageOverlay ?? { ...DEFAULT_IMAGE_OVERLAY },
    };
  }
  return block;
}

function fillRow(row: Row, settings: EmailDocument['settings']): Row {
  return {
    ...row,
    backgroundImageOverlay: row.backgroundImageOverlay ?? { ...DEFAULT_IMAGE_OVERLAY },
    columns: row.columns.map(column => ({
      ...column,
      backgroundImageOverlay: column.backgroundImageOverlay ?? { ...DEFAULT_IMAGE_OVERLAY },
      blocks: column.blocks.map(block => normalizeBlock(block, settings)),
    })),
  };
}

function fillSection(
  section: DocumentSection,
  settings: EmailDocument['settings'],
): DocumentSection {
  if (isGroup(section)) {
    const { ...group } = section;

    return {
      ...group,
      backgroundImageOverlay: group.backgroundImageOverlay ?? { ...DEFAULT_IMAGE_OVERLAY },
      rows: group.rows.map(row => fillRow(row, settings)),
    };
  }
  return fillRow(section, settings);
}

export function fillDocumentDefaults(doc: {
  schemaVersion?: number;
  settings: Partial<EmailSettings>;
  sections: EmailDocument['sections'];
}): EmailDocument {
  const settings = normalizeSettings(doc.settings);

  const sections = doc.sections.map(section => fillSection(section, settings));

  return {
    schemaVersion: doc.schemaVersion ?? EMAIL_DOCUMENT_SCHEMA_VERSION,
    settings,
    sections,
  };
}
